// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { ADT_ALLOW_LISTED_URLS } from "../services/Constants";
import jsonlint from "jsonlint";

export function readFile(file) {
  // Always return a Promise
  return new Promise((resolve, reject) => {
    let content = "";
    const reader = new FileReader();
    // Wait till complete
    reader.onloadend = function (e) {
      content = e.target.result;

      try {
        const result = jsonlint.parse(content);
        resolve(result);
      } catch (exp) {
        reject(exp);
      }
    };
    // Make sure to handle error states
    reader.onerror = function (e) {
      reject(e);
    };
    reader.readAsText(file);
  });
}

export function sortArray(array, ...propertyNames) {
  array.sort((a, b) => {
    for (const p of propertyNames) {
      const pA = a[p].toUpperCase();
      const pB = b[p].toUpperCase();
      if (pA < pB) {
        return -1;
      }
      if (pA > pB) {
        return 1;
      }
    }

    return 0;
  });
}

export function capitalizeName(name) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function getUniqueRelationshipId(relationship) {
  return `${relationship.$sourceId}_${relationship.$relationshipId}`;
}

export function addNavigator(cy, navigationOptions, container) {
  const nav = cy.navigator({ ...navigationOptions, container });

  /* Sometimes the navigator thumbnail update fails as the DOM is not
     in the correct state to be modified. As a workaround, this function
     replaces the rendering event handlers added by the navigator with
     one that is wrapped in a try/catch. */
  const fn = nav._onRenderHandler;
  cy.offRender(fn);

  const newFn = function () {
    try {
      fn();
      // Add navigator image alt for accessibility purposes
      const imgEl = document.getElementById(container.replace("#", "")).getElementsByTagName("img")[0];
      if (imgEl) {
        if (imgEl.src) {
          imgEl.setAttribute("alt", "graph navigator");
        } else {
          imgEl.removeAttribute("alt");
        }
      }
    } catch (e) {
      // Retry the render function after a timeout
      setTimeout(newFn, nav.options.rerenderDelay);
    }
  };

  nav._onRenderHandler = newFn;
  cy.onRender(newFn);
}

export function createEnum(values) {
  const enumObject = {};
  for (const val of values) {
    enumObject[val] = val;
  }
  return Object.freeze(enumObject);
}

export function getDebugLogger(
  context,
  enabled
) {
  if (!enabled) {
    return () => null;
  }

  return (
    level,
    message,
    ...args
  ) => {
    const formattedMessage = `[EX-DEBUG][${context}] ${message}`;
    switch (level) {
      case "debug":
        // eslint-disable-next-line no-console
        console.debug(formattedMessage, ...args);
        break;
      case "error":
        // eslint-disable-next-line no-console
        console.error(formattedMessage, ...args);
        break;
      case "warn":
        // eslint-disable-next-line no-console
        console.warn(formattedMessage, ...args);
        break;
      default:
        // eslint-disable-next-line no-console
        console.info(formattedMessage, ...args);
        break;
    }
  };
}

export function convertSeverityToAppInsightsSeverity(severityLevel) {
  switch (severityLevel) {
    case "Verbose":
      return SeverityLevel.Verbose;
    case "Information":
      return SeverityLevel.Information;
    case "Warning":
      return SeverityLevel.Warning;
    case "Error":
      return SeverityLevel.Error;
    case "Critical":
      return SeverityLevel.Critical;
    default:
      return SeverityLevel.Information;
  }
}

/**
 * Validate if URL is Explorer for CORS enabling
 */
export function validateExplorerOrigin(origin) {
  return (
    origin
    && (origin === ADT_ALLOW_LISTED_URLS.DEV
    || origin === ADT_ALLOW_LISTED_URLS.PROD)
  );
}
